<template>
    <!-- Painel lateral do Mapa do Site: diálogo acessível (foco preso, Esc fecha, foco volta ao botão). -->
    <Teleport to="body">
        <Transition enter-active-class="transition-opacity duration-200 motion-reduce:transition-none" enter-from-class="opacity-0"
            leave-active-class="transition-opacity duration-200 motion-reduce:transition-none" leave-to-class="opacity-0">
            <div v-if="aberto" class="fixed inset-0 z-[70] bg-black/50 print:hidden" aria-hidden="true" @click="fechar"></div>
        </Transition>
        <Transition enter-active-class="transition-transform duration-300 ease-out motion-reduce:transition-none" enter-from-class="translate-x-full"
            leave-active-class="transition-transform duration-200 ease-in motion-reduce:transition-none" leave-to-class="translate-x-full">
            <div v-if="aberto" ref="painel" role="dialog" aria-modal="true" :aria-labelledby="idTitulo"
                class="fixed inset-y-0 right-0 z-[71] w-full sm:w-[380px] max-w-full bg-white text-gray-900 shadow-2xl flex flex-col print:hidden"
                @keydown="aoTeclar">
                <div class="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200">
                    <h2 :id="idTitulo" class="text-lg font-bold text-admin-primary">Mapa do Site</h2>
                    <button type="button" class="w-10 h-10 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-admin-primary/40"
                        aria-label="Fechar o mapa do site" @click="fechar">
                        <X class="w-5 h-5" />
                    </button>
                </div>

                <div class="flex-1 overflow-y-auto px-5 py-4">
                    <MapaDoSiteLista v-if="grupos" ref="lista" :grupos="grupos" />
                    <div v-else-if="erro" class="py-8 text-center text-sm text-gray-700" role="alert">
                        Não foi possível carregar o mapa.
                        <button type="button" class="ml-1 font-semibold text-admin-primary underline" @click="carregar">Tentar de novo</button>
                    </div>
                    <div v-else class="space-y-4" aria-busy="true" aria-label="Carregando o mapa do site">
                        <div class="h-11 rounded-xl bg-gray-100 animate-pulse"></div>
                        <div v-for="n in 4" :key="n" class="space-y-2">
                            <div class="h-4 w-40 rounded bg-gray-200 animate-pulse"></div>
                            <div class="h-3 w-56 rounded bg-gray-100 animate-pulse"></div>
                            <div class="h-3 w-48 rounded bg-gray-100 animate-pulse"></div>
                        </div>
                    </div>
                </div>

                <div class="px-5 py-3 border-t border-gray-200">
                    <Link :href="route('mapa-do-site.public')" class="text-sm font-semibold text-admin-primary hover:underline" @click="fechar">
                        Ver o mapa completo em uma página
                    </Link>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import { X } from 'lucide-vue-next'
import MapaDoSiteLista from '@/Components/Welcom/MapaDoSiteLista.vue'

const props = defineProps({
    aberto: { type: Boolean, default: false },
})

const emit = defineEmits(['fechar'])

const idTitulo = useId()
const painel = ref(null)
const lista = ref(null)
const erro = ref(false)
const page = usePage()

// O mapa vem de uma prop opcional do Inertia: só é calculado no servidor quando o painel abre.
const grupos = computed(() => page.props.mapaDoSite ?? null)

function carregar() {
    erro.value = false
    router.reload({
        only: ['mapaDoSite'],
        onSuccess: () => nextTick(() => lista.value?.focarBusca()),
        onError: () => { erro.value = true },
        onException: () => { erro.value = true },
    })
}

let elementoAnterior = null

watch(() => props.aberto, async (aberto) => {
    if (aberto) {
        elementoAnterior = document.activeElement
        document.body.style.overflow = 'hidden'
        if (!grupos.value) carregar()
        await nextTick()
        if (grupos.value) lista.value?.focarBusca()
        else painel.value?.querySelector('button')?.focus()
        return
    }
    document.body.style.overflow = ''
    elementoAnterior?.focus?.()
})

onBeforeUnmount(() => { document.body.style.overflow = '' })

function fechar() {
    emit('fechar')
}

const SELETOR_FOCAVEL = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Esc fecha; Tab e Shift+Tab circulam só dentro do painel.
function aoTeclar(evento) {
    if (evento.key === 'Escape') {
        evento.preventDefault()
        fechar()
        return
    }
    if (evento.key !== 'Tab' || !painel.value) return

    // Ignora o que está dentro de grupos recolhidos (v-show deixa display: none).
    const focaveis = [...painel.value.querySelectorAll(SELETOR_FOCAVEL)].filter(elemento => !elemento.closest('[style*="display: none"]'))
    if (!focaveis.length) return
    const primeiro = focaveis[0]
    const ultimo = focaveis[focaveis.length - 1]

    if (evento.shiftKey && document.activeElement === primeiro) {
        evento.preventDefault()
        ultimo.focus()
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault()
        primeiro.focus()
    }
}
</script>
